/* eslint-disable no-unused-vars */
import { useEffect } from 'react';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import * as Sentry from "@sentry/react";
// redux
import { dispatch, useSelector } from '../redux/store';
import { updateError } from '../redux/slices/user';
import { leadPage } from '../redux/slices/lead';
//
import { Iconify } from '.';
import isPublicRoute from '../utils/isPublicRoute';

// Contract codes whose 4xx responses are captured to Sentry (see the capture gate).
const ALERTED_CONTRACT_CODES = new Set(['attendance_confirmation_required', 'template_variable_unmapped']);

// Cascade detector: when an auth failure blows up multiple unrelated API
// calls at once, they all land here in rapid succession. We let the first
// CASCADE_THRESHOLD events through to Sentry and suppress the rest while
// the window is still open so a single auth failure doesn't produce 8+
// duplicate issues.
let lastErrorTime = 0;
let errorCountInWindow = 0;
const CASCADE_WINDOW_MS = 1000;
const CASCADE_THRESHOLD = 3;

export default function ErrorHandler() {
    const { error } = useSelector(state => state.user);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    const navigate = useNavigate();
    const { pathname } = useLocation();

    // `leadPracticeId` lets a toast point at a lead in another practice (e.g. after a transfer).
    const handleViewLeadDetail = (key, id, leadPracticeId) => {
        closeSnackbar(key);
        dispatch(leadPage({}));
        navigate(`/dashboard/practice/${leadPracticeId || practiceId}/lead-detail/${id}`, { state: { shouldRedirectToOverview: true } });
    };

    useEffect(() => {
        // Suppress all snackbar notifications on patient-facing public pages
        if (isPublicRoute(pathname)) return;

        if (error !== null) {
            const AUTH_ERROR_PATTERNS = [
                'token is invalid or expired',
                'token is blacklisted',
                'given token not valid',
                'authentication credentials were not provided',
                'session has timed out',
                'refresh token not valid',
                'no active account found',
                'user inactive',
                // Refresh token validation — DRF returns {"refresh":["This field may not be null."]}
                // when localStorage refreshToken is null/expired. The apiCall.js refresh handler
                // already shows "Session has timed Out!" and redirects — this prevents the raw
                // JSON from also appearing as a second toast from the calling thunk.
                '"refresh"',
                'this field may not be null',
            ];
            // Extract the human-readable error string. When error.message is already
            // an object envelope ({success, message, detail, ...}) read the nested
            // property directly — avoids a wasteful stringify→JSON.parse round-trip.
            const rawMsgSrc = error.message;
            const rawErrValue = (rawMsgSrc !== null && typeof rawMsgSrc === 'object')
                ? (rawMsgSrc.detail || rawMsgSrc.message || rawMsgSrc.error || '')
                : (rawMsgSrc?.detail || rawMsgSrc || '');
            const rawErrorMsg = typeof rawErrValue === 'string' ? rawErrValue : JSON.stringify(rawErrValue);
            const isAuthStatus = error?.status === 401 ||
                (error?.status === 403 && (rawErrorMsg.toLowerCase().includes('permission') || rawErrorMsg.toLowerCase().includes('credentials')));
            const isAuthError = isAuthStatus || AUTH_ERROR_PATTERNS.some(
                p => rawErrorMsg.toLowerCase().includes(p)
            );

            // Intentional request cancellations (AbortController / axios cancel)
            // are not failures. Clear them before touching the module-global
            // cascade counters so cancellations cannot suppress a following error.
            const isCancellation = error?.name === 'CanceledError'
                || error?.name === 'AbortError'
                || error?.code === 'ERR_CANCELED';
            if (isCancellation) {
                dispatch(updateError(null));
                return;
            }

            // Cascade detector — auth failures often blow up 4-10 unrelated
            // API calls simultaneously. Let the first CASCADE_THRESHOLD
            // through, suppress the rest within the window.
            const now = Date.now();
            if (now - lastErrorTime < CASCADE_WINDOW_MS) {
                errorCountInWindow += 1;
            } else {
                errorCountInWindow = 1;
            }
            lastErrorTime = now;
            const isInCascade = errorCountInWindow > CASCADE_THRESHOLD;

            const isNotFound = error.status === 404 || rawErrorMsg.includes('Not found.') || rawErrorMsg === '{"detail":"Not found."}';
            // Narrow the archived-lead heuristic.
            //
            // The previous check matched any string containing
            // "object does not exist" or "invalid pk" — which is the *generic*
            // DRF PrimaryKeyRelatedField failure message. That made the
            // "This lead has been archived" snackbar fire on every unrelated
            // FK validation error (e.g. the form-builder create call failing
            // because the FormFieldType lookup row was missing).
            //
            // A real archived-lead error either:
            //   * comes from a thunk that explicitly attaches ``error.lead``
            //     (a lead id, used to render the "Show lead" button), OR
            //   * includes the word "lead" in the BE error string itself.
            // Require at least one of those signals *in addition to* the
            // FK-style phrase before relabelling the user-visible message.
            const looksLikeFkError = rawErrorMsg.toLowerCase().includes('object does not exist')
                || rawErrorMsg.toLowerCase().includes('invalid pk');
            const mentionsLead = rawErrorMsg.toLowerCase().includes('lead') || Boolean(error?.lead);
            const isArchivedLead = looksLikeFkError && mentionsLead;
            let errorMsg = rawErrorMsg;
            // Guard against JSON.stringify(null) → "null" from thunks using
            // `JSON.stringify(response?.data?.message) || 'Something went wrong'`
            if (errorMsg === 'null' || errorMsg === 'undefined' || errorMsg === '"null"') {
                errorMsg = 'Something went wrong';
            }
            // Normalise raw JSON-envelope messages to a friendly display string.
            // Thunks occasionally dispatch the raw API response object serialised as a
            // string (e.g. '{"success":false,"message":"An internal error occurred"}').
            // Show users a clean message; the sanitized debugInfo is sent to Sentry
            // (5xx only) — never the full envelope with PII/credentials.
            if (errorMsg.startsWith('{') || errorMsg.startsWith('[')) {
                try {
                    const parsed = JSON.parse(errorMsg);
                    const extracted = parsed?.message || parsed?.detail || parsed?.error;
                    errorMsg = (typeof extracted === 'string' && extracted && !extracted.startsWith('{'))
                        ? extracted
                        : 'Something went wrong';
                } catch {
                    errorMsg = 'Something went wrong';
                }
            }
            // A bare HTML document (a server 404/500 page — nginx or Django default —
            // returned instead of JSON) must never be shown raw to the user, and it
            // shouldn't reach Sentry as a "UI Popup Error" (JAVASCRIPT-REACT-RJ). Its
            // body carries no parseable HTTP status, so it otherwise slips the 4xx
            // capture gate below (status comes through as null → treated as network).
            const looksLikeHtmlBody = (() => {
                const t = (errorMsg || '').trim().toLowerCase();
                return t.startsWith('<!doctype') || t.startsWith('<html') || t.includes('<title>not found</title>');
            })();
            if (isArchivedLead) errorMsg = 'This lead has been archived by another user.';
            else if (isNotFound || looksLikeHtmlBody) errorMsg = 'This record could not be found — it may have been deleted or moved.';

            const EXPECTED_USER_MESSAGES = [
                'please select any one tab', // "Please select any one Tab" — 'any one' broke the old match
                'please select tab',         // keep as fallback for other tab-selection variants
                'not available for communication',
                'please add archive notes',
                'please select an archive reason',
                'unable to proceed',
                'please complete the required fields', // "Please complete the required fields before continuing."
                'scheduled time must be in the future', // "Scheduled time must be in the future"
            ];
            const isExpectedMessage = EXPECTED_USER_MESSAGES.some(
                p => rawErrorMsg.toLowerCase().includes(p)
            );
            const isWarning = error?.severity === 'warning' || error?.success === 'warning';
            let notificationVariant = 'error';
            if (isWarning) notificationVariant = 'warning';
            else if (error?.success) notificationVariant = 'success';

            if (!isWarning && error?.success !== 'success' && !error?.success) {
                // Gate Sentry capture on HTTP status:
                //   • 5xx (server errors) → always capture
                //   • no status / status 0 (network timeout, offline) → capture
                //   • 4xx (400/422 validation, etc.) → show toast only, do NOT capture
                // This is the primary fix — message-string matching in EXPECTED_USER_MESSAGES
                // is now defense-in-depth, not the sole gate.
                const httpStatus = error?.status;
                // Coerce defensively — some thunks may attach status as a string
                // ("500" from JSON parsing). Number.isFinite guards against NaN/Infinity.
                const code = Number(httpStatus);
                const isServerError = Number.isFinite(code) && code >= 500;
                const isNetworkError = httpStatus === undefined || httpStatus === null || code === 0;
                // Backend contract codes we alert on (B7, 869fmawux): a 409 is
                // normally toast-only, but a spike of these means a release broke
                // a legacy client path (23 Oct: first failure 09:43, reported hours
                // later). Captured so the Sentry rule on tag error_code can fire.
                // Thunks dispatch `{ message, success:false, cause }` with the
                // status/code on the cause (attendanceResponse attaches them
                // there), so read the contract code from either level.
                const ownCode = typeof error?.code === 'string' ? error.code : '';
                const causeCode = typeof error?.cause?.code === 'string' ? error.cause.code : '';
                const contractCode = ownCode || causeCode || null;
                const isAlertedContractCode = ALERTED_CONTRACT_CODES.has(contractCode);
                const shouldCaptureSentry = isServerError || isNetworkError || isAlertedContractCode;

                if (shouldCaptureSentry && !isAuthError && !isNotFound && !looksLikeHtmlBody && !isArchivedLead && !isExpectedMessage && !isInCascade) {
                    // Build a SANITIZED error for Sentry — never send the raw cause
                    // (which may be an axios AxiosError carrying config.data = patient PII
                    // and config.headers.Authorization = JWT).
                    // Strategy: copy the original stack (for Sentry grouping) onto a fresh
                    // Error that carries only message + name — no .config, no credentials.
                    const diagnosticMessage = typeof error?.diagnosticMessage === 'string'
                        ? error.diagnosticMessage
                        : null;
                    let toCapture;
                    if (diagnosticMessage && error?.cause instanceof Error) {
                        toCapture = Object.assign(
                            new Error(diagnosticMessage),
                            { name: 'UI Popup Error' }
                        );
                        // The helper copied and sanitized this stack. Never read
                        // transport error objects or Axios request configuration here.
                        toCapture.stack = error.cause.stack;
                    } else if (error?.cause instanceof Error) {
                        toCapture = Object.assign(
                            new Error(error.cause.message || errorMsg),
                            { name: 'UI Popup Error' }
                        );
                        // Preserve the established call-site stack contract for
                        // existing dispatchers that do not use the new envelope.
                        toCapture.stack = error.cause.stack;
                    } else {
                        toCapture = Object.assign(new Error(errorMsg), { name: 'UI Popup Error' });
                    }
                    Sentry.captureException(toCapture, {
                        fingerprint: [
                            'ui-popup-error',
                            // First 80 chars of message (deduplicates dynamic IDs in messages)
                            (diagnosticMessage || errorMsg || 'unknown').toString().slice(0, 80),
                            // HTTP status if available
                            String(error?.status ?? 'no-status'),
                            String(error?.method ?? 'no-method'),
                            // Sanitized failing endpoint — groups status:null network/timeout
                            // events by real endpoint instead of collapsing into one blob.
                            String(error?.endpoint ?? 'no-endpoint'),
                            // Route — prevents unrelated crashes on different pages merging
                            window.location.pathname,
                        ],
                        tags: {
                            feature: "Handled Exception - Possible UI Snackbar",
                            route: window.location.pathname,
                            ...(error?.status && { http_status: error.status }),
                            ...(error?.endpoint && { endpoint: error.endpoint }),
                            ...(error?.method && { http_method: error.method }),
                            // Backend contract code (e.g. attendance_confirmation_required,
                            // template_variable_unmapped) as a TAG so Sentry alert rules can
                            // key on it (B7, 869fmawux); debugInfo below is not filterable.
                            ...(contractCode && { error_code: contractCode.slice(0, 64) }),
                        },
                        extra: {
                            // Sanitized debug info only — NEVER JSON.stringify(error) which
                            // would walk into cause.config.data (PII) and cause.config.headers
                            // (Authorization JWT) if cause is a raw axios error.
                            debugInfo: JSON.stringify({
                                message: errorMsg,
                                status: error?.status ?? null,
                                code: error?.code ?? null,
                                name: error?.name ?? null,
                                causeClass: error?.causeClass ?? null,
                                diagnosticMessage,
                                endpoint: error?.endpoint ?? null,
                                method: error?.method ?? null,
                            }),
                        },
                    });
                }
            }

            if (isAuthError) {
                dispatch(updateError(null));
                return;
            }

            if (!errorMsg) return; // Don't show empty snackbars
            // notistack closes/removes snackbars BY ID, and every toast here shares
            // 'success-notification' by default - so when an earlier toast times out or is
            // dismissed it takes any later same-id toast with it. A multi-step flow that
            // shows progress toasts followed by a final outcome gives the outcome its own
            // `toastId` and names the progress toasts to clear via `dismissToastId` (id or array).
            if (error.dismissToastId) [].concat(error.dismissToastId).forEach((toastId) => closeSnackbar(toastId));
            enqueueSnackbar(errorMsg, {
                variant: notificationVariant, anchorOrigin: { vertical: 'top', horizontal: 'center' }, autoHideDuration: error.timeout, id: error.toastId || 'success-notification',
                action: (key) =>
                (error.lead ? <>
                    <Button onClick={() => handleViewLeadDetail(key, error.lead, error.leadPracticeId)} variant='outlined' size="small"  >
                        {error.leadActionLabel || 'Show lead'} <Iconify icon={'ep:user-filled'} height={15} width={15} sx={{ margin: '0px 8px', cursor: 'pointer' }} />
                    </Button>
                    <Iconify onClick={() => closeSnackbar(key)} icon={'radix-icons:cross-2'} height={15} width={15} sx={{ margin: '0px 8px', cursor: 'pointer' }} />
                </> :
                    <Iconify onClick={() => closeSnackbar(key)} icon={'radix-icons:cross-2'} height={15} width={15} sx={{ margin: '0px 8px', cursor: 'pointer' }} />
                )
            }, true);
            setTimeout(() => {
                dispatch(updateError(null));
            }, 3000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);
    return (
        <></>
    );
}
